/**
 * RESPONSIBILITY: when a repeating date next falls, after a given moment.
 *
 * Separate from reading it, because walking the calendar is the one job here that has to
 * think about months of unequal length, last weekdays and leap years.
 *
 * Null means "not again" — a rule half made, or one that cannot fall inside the horizon.
 * The events page reads that as having no date, exactly as a missing one.
 */
import { hasTime, type RepeatRule } from './repeat-rule';
import { readRepeat } from './repeat-parse';

/**
 * Nine years, a day at a time. 29 February can be eight years away across 2100, which
 * is not a leap year; a day-by-day walk is a few thousand cheap steps at worst and one
 * in the usual case.
 */
const HORIZON_DAYS = 366 * 9;

const daysIn = (y: number, m: number): number => new Date(y, m + 1, 0).getDate();

function onDay(r: RepeatRule, d: Date): boolean {
  switch (r.freq) {
    case 'weekly':
      return r.days.includes(d.getDay());
    case 'monthly':
      if (r.nth !== undefined) {
        if (d.getDay() !== r.days[0]) return false;
        if (r.nth === -1) return d.getDate() + 7 > daysIn(d.getFullYear(), d.getMonth());
        return Math.floor((d.getDate() - 1) / 7) + 1 === r.nth;
      }
      // The 31st does not happen in a 30-day month — the calendar convention, and what a
      // person means by "the 31st". Moving it to the 30th would be a second rule.
      return d.getDate() === r.monthDay;
    case 'yearly':
      return d.getMonth() + 1 === r.month && d.getDate() === r.monthDay;
  }
}

/**
 * True when the rule says enough to fall at all. "Weekly" with no day, or 31 April, is
 * a rule a person is part-way through, and it has no next date rather than a wrong one.
 */
export function isComplete(r: RepeatRule): boolean {
  switch (r.freq) {
    case 'weekly': return r.days.length > 0;
    case 'monthly': return r.nth !== undefined ? r.days.length === 1 : r.monthDay !== undefined;
    case 'yearly':
      // 2000 is a leap year, so every date that ever exists exists in it.
      return r.month !== undefined && r.monthDay !== undefined
        && r.monthDay <= daysIn(2000, r.month - 1);
  }
}

/**
 * The next time the rule falls after `from`, or null.
 *
 * With a time of day: strictly after `from`, so asking again walks forward. Without one
 * it is a day, and today still counts while today lasts — an all-day gathering has not
 * finished at one minute past midnight.
 */
export function nextRepeat(rule: RepeatRule | string | undefined, from: Date): Date | null {
  const r = typeof rule === 'string' ? readRepeat(rule) : rule;
  if (!r || !isComplete(r)) return null;

  const day = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  for (let i = 0; i <= HORIZON_DAYS; i++) {
    if (onDay(r, day)) {
      if (!hasTime(r)) return new Date(day);
      const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), r.hour, r.minute);
      if (at > from) return at;
    }
    day.setDate(day.getDate() + 1);
  }
  return null;
}
